import { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"

import AuthAlert from "@/components/tuw/auth/AuthAlert"
import { AuthHeading } from "@/components/tuw/auth/AuthHeading"
import CodeInput, { CODE_LENGTH } from "@/components/tuw/onboarding/CodeInput"
import { Button } from "@/components/ui/button"
import { postedThen } from "@/lib/openHouse/analytics"
import { confirmByCode, correctEmail, PROJECTS_PATH, resendConfirmation, signInToContinue } from "@/lib/openHouse/guest"
import {
  CONFIRMATION as C, CONFIRMATION_REFUSALS, EMAIL_CORRECTION as E, POST_EXPIRED as X, POST_EXPIRED_REASONS,
} from "@/lib/openHouse/wording"
import { signOut } from "@/lib/orderActions"
import { fill, VR } from "@/lib/viewingRoom/wording"
import EmailCorrection from "./EmailCorrection"

// Key turn's waiting screen (spec 6f-c; BRIEF-2 step 6; guest-wording §4): a
// posted draft waits, hidden from writers, until the guest confirms the email
// with its code here or with its button anywhere. `waiting` is the server's
// state for the cookie's reservation (OpenHouse::Confirmation.waiting): the
// address the email went to, whether it went, the deadline as the email shows
// it, the resend's cooldown and whether its cap is reached, or only
// `confirmed` or `expired`. Confirmed, the page goes to the posted project.
//
// Its "Correct email address" opens the correction form in its place
// (EmailCorrection, P3-H8). A draft carried into an existing account
// (`continued`) goes on to that account (sign in and continue). Confirming,
// resending and correcting are never in flight together.
//
// Past the post's deadline (Sundown, P3-H10), the screen ends in §7's lines,
// whose "Start a new project" is the page's onStartNew.
//
// `flow` replaces the post's transport and lines for another kind of
// confirmation (Viewing Room's account-only enrollment,
// lib/viewingRoom/enrollment.js enrollmentFlow): its confirm, resend, correct
// and signIn; its heading, lead, submit, deliveryFailed and codeLocked lines;
// `refusal(err)` for the alert; `usedUp` when no email can be sent again;
// `accountOnly` for the deleted account's screen (AccountExpired) in place of
// the deleted post's; `already` ({ body, button }, the button signing in) in
// place of the confirmed screen; `correction` for EmailCorrection;
// and a support link (`supportHref`, `supportLabel`) under account_conflict.
// Without a deadline in `waiting`, no deadline lines show.
//
// A confirmed post that kept an invited freelancer answers with `invitation`
// (OpenHouse::InvitationDelivery's outcome); the page then shows what became
// of it (InvitationOutcome) instead of going straight to the project.

// The confirmed account's way on, here and on the link page. The waiting
// screen is the guest page, never signed in, so it says to sign in here.
export function AlreadyConfirmed({ href = PROJECTS_PATH, body = C.alreadyConfirmed.body }) {
  return (
    <>
      <AuthHeading title={C.alreadyConfirmed.heading}>{body}</AuthHeading>
      <Button asChild>
        <a href={href}>{C.alreadyConfirmed.button}</a>
      </Button>
    </>
  )
}

export function WrongAccount() {
  return (
    <>
      <AuthAlert tone="bad">{C.wrongAccount}</AuthAlert>
      <Button variant="outline" onClick={signOut}>{C.signOut}</Button>
    </>
  )
}

const MUTED = "text-[13px] leading-[1.55] text-muted-foreground"

// Viewing Room wording §9, here and on the link page: the project is live,
// and what became of the invitation, by the server's outcome. An outcome
// with no title of its own (pending, failed) is one line.
export function InvitationOutcome({ invitation, projectHref }) {
  const words = VR.invitation[invitation.outcome] ?? VR.invitation.pending
  const body = fill(words.body, { pen_name: invitation.penName })

  return (
    <>
      {words.title ? (
        <AuthHeading title={words.title}>{body}</AuthHeading>
      ) : (
        <AuthAlert tone={invitation.outcome === "failed" ? "bad" : "quiet"}>{body}</AuthAlert>
      )}
      {words.next && <p className={MUTED}>{words.next}</p>}
      <Button asChild>
        <a href={projectHref}>{VR.invitation.viewProject}</a>
      </Button>
      <Button asChild variant="outline">
        <a href={invitation.findFreelancers}>{VR.invitation.findAnother}</a>
      </Button>
    </>
  )
}

// After the confirmation's analytics: the invitation's outcome when there is one, else the project.
export function afterConfirmed({ redirect, posted, invitation }, showOutcome) {
  postedThen(posted, () => (invitation ? showOutcome({ invitation, redirect }) : window.location.assign(redirect)))
}

// OpenHouse::Confirmation::POST_LIFETIME, in days.
const POST_LIFETIME_DAYS = 30
const START_PATH = "/post-project"
const SIGN_IN_PATH = "/login"
export const RESTART_PATH = "/find-freelancers"

// A pending post past its deadline, here and on the link page (guest-wording
// §7): deleted, with a fresh start and no promise of recovery.
// `oldConfirmation` adds that the code or link used no longer confirms it.
export function PostExpired({ oldConfirmation = false, onStartNew }) {
  return (
    <>
      <AuthHeading title={X.heading}>{X.body(POST_LIFETIME_DAYS)}</AuthHeading>
      {oldConfirmation && <AuthAlert tone="bad">{X.oldConfirmation}</AuthAlert>}
      <p className={MUTED}>{X.recovery}</p>
      {onStartNew ? (
        <Button onClick={onStartNew}>{X.startNew}</Button>
      ) : (
        <Button asChild>
          <a href={START_PATH}>{X.startNew}</a>
        </Button>
      )}
      <p className={MUTED}>{X.existingAccount}</p>
      <Button asChild variant="outline">
        <a href={SIGN_IN_PATH}>{X.signIn}</a>
      </Button>
    </>
  )
}

// An account-only enrollment past its deadline, here and on its link page:
// deleted, nothing sent, and a fresh start from freelancer search.
export function AccountExpired({ oldConfirmation = false }) {
  const A = VR.confirmation.accountExpired
  return (
    <>
      <AuthHeading title={A.title}>{fill(A.body, { days: POST_LIFETIME_DAYS })}</AuthHeading>
      {oldConfirmation && <AuthAlert tone="bad">{A.oldLink}</AuthAlert>}
      <p className={MUTED}>{A.recovery}</p>
      <Button asChild>
        <a href={RESTART_PATH}>{A.startAgain}</a>
      </Button>
    </>
  )
}

// setTimeout's longest delay; a later deadline is checked again then.
const LONGEST_TIMER = 2 ** 31 - 1

// The waiting screen's column, also around the guest page's deleted-post screen.
export function WaitingColumn({ children }) {
  return <div className="mx-auto flex max-w-[440px] flex-col gap-[22px] px-8 pt-12 pb-14 max-[561px]:px-4">{children}</div>
}

const POST_FLOW = {
  confirm: confirmByCode, resend: resendConfirmation, correct: correctEmail, signIn: signInToContinue,
  heading: C.heading, lead: C.saved, submit: C.submit, deliveryFailed: C.deliveryFailed, codeLocked: C.code.expired,
}

// A flow's own end: a line and its sign-in button.
function SignInEnd({ heading, body, button, onSignIn }) {
  return (
    <>
      {heading ? <AuthHeading title={heading}>{body}</AuthHeading> : <AuthAlert tone="bad">{body}</AuthAlert>}
      <Button onClick={() => onSignIn()}>{button}</Button>
    </>
  )
}

export default function WaitingScreen({ waiting: initial, onStartNew, flow: given }) {
  const flow = { ...POST_FLOW, ...given }
  const [waiting, setWaiting] = useState(initial)
  // A refusal that ends the form: already | wrongAccount | expired | refusedExpired (a code's).
  const [ending, setEnding] = useState(null)
  const [now, setNow] = useState(() => Date.now())
  const [signedInEarly, setSignedInEarly] = useState(false)

  // A sign in before confirming lands here marked (OpenHouse::FrontPorchController#signed_in_early).
  useEffect(() => {
    if (window.location.hash !== "#signed-in-early") return
    setSignedInEarly(true)
    window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search)
  }, [])
  const [code, setCode] = useState("")
  const [codeKey, setCodeKey] = useState(0) // a new code empties the cells
  const [codeError, setCodeError] = useState(null)
  const [locked, setLocked] = useState(false)
  const [alert, setAlert] = useState(null)
  const [support, setSupport] = useState(false) // the alert is account_conflict's, with flow's support link
  const [notice, setNotice] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [cooldown, setCooldown] = useState(initial.cooldownSeconds || 0)
  const [correcting, setCorrecting] = useState(false)
  const [undelivered, setUndelivered] = useState(false) // a correction's replacement email didn't go
  const [outcome, setOutcome] = useState(null) // confirmed with an invitation: { invitation, redirect }

  useEffect(() => {
    if (waiting.continued) signInToContinue()
  }, [waiting.continued])

  // The post's deadline passing while the page is open ends the form at it.
  const deadlineAt = waiting.deadline ? Date.parse(waiting.deadline) : null
  useEffect(() => {
    if (deadlineAt === null || deadlineAt <= now) return undefined
    const id = setTimeout(() => setNow(Date.now()), Math.min(deadlineAt - now, LONGEST_TIMER))
    return () => clearTimeout(id)
  }, [deadlineAt, now])

  // The view follows the current state: any answer can say confirmed or expired.
  let view = "form"
  if (outcome) view = "outcome"
  else if (ending) view = ending
  else if (waiting.continued) view = "continued"
  else if (waiting.confirmed) view = "already"
  else if (waiting.expired || (deadlineAt !== null && deadlineAt <= now)) view = "expired"

  useEffect(() => {
    if (cooldown <= 0) return undefined
    const id = setTimeout(() => setCooldown((left) => left - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  const refuse = (err) => {
    if (err.reason === "already_confirmed") return setEnding("already")
    if (err.reason === "wrong_account") return setEnding("wrongAccount")
    if (POST_EXPIRED_REASONS.includes(err.reason)) return setEnding("expired")
    setSupport(!!flow.supportHref && err.reason === "account_conflict")
    setAlert(flow.refusal ? flow.refusal(err) : (CONFIRMATION_REFUSALS[err.reason] ?? C.failed))
  }

  const submit = async (event) => {
    event.preventDefault()
    if (submitting || resending || locked) return
    setAlert(null)
    setNotice(null)
    if (!code) return setCodeError(C.code.required)
    if (code.length < CODE_LENGTH) return setCodeError(C.code.incomplete(CODE_LENGTH))

    setCodeError(null)
    setSubmitting(true)
    try {
      afterConfirmed(await flow.confirm(code), setOutcome)
    } catch (err) {
      setSubmitting(false)
      if (err.reason === "code_invalid") return setCodeError(C.code.invalid)
      if (err.reason === "code_locked") return setLocked(true)
      if (POST_EXPIRED_REASONS.includes(err.reason)) return setEnding("refusedExpired")
      refuse(err)
    }
  }

  // A new proof: the old code is gone, the code cells empty.
  const renewed = (state) => {
    setWaiting(state)
    setCooldown(state.cooldownSeconds)
    setLocked(false)
    setCodeError(null)
    setCode("")
    setCodeKey((n) => n + 1)
  }

  const corrected = (result, { delivered }) => {
    setCorrecting(false)
    setAlert(null)
    renewed(result.waiting)
    setUndelivered(!delivered)
    setNotice(delivered ? E.success(result.email) : null)
  }

  const resend = async () => {
    if (submitting || resending) return
    setAlert(null)
    setNotice(null)
    setResending(true)
    try {
      const result = await flow.resend()
      setWaiting(result.waiting)
      if (!result.waiting.email) return
      renewed(result.waiting)
      setUndelivered(false)
      setNotice(C.resendSuccess(result.waiting.email))
    } catch (err) {
      if (err.reason === "cooldown") setCooldown(err.data?.retry_after_seconds || 0)
      else if (err.reason === "too_many_resends") setWaiting((w) => ({ ...w, resendsUsedUp: true }))
      else if (err.reason === "delivery_failed") setAlert(C.resendFailed)
      else refuse(err)
    } finally {
      setResending(false)
    }
  }

  let body = null // continued: on its way to the account
  if (view === "outcome") body = <InvitationOutcome invitation={outcome.invitation} projectHref={outcome.redirect} />
  else if (view === "already" && flow.already) {
    body = <SignInEnd heading={C.alreadyConfirmed.heading} {...flow.already} onSignIn={flow.signIn} />
  } else if (view === "already") body = <AlreadyConfirmed body={C.alreadyConfirmed.bodyNotSignedIn} />
  else if (view === "wrongAccount") body = <WrongAccount />
  else if ((view === "expired" || view === "refusedExpired") && flow.accountOnly) {
    body = <AccountExpired oldConfirmation={view === "refusedExpired"} />
  } else if (view === "expired" || view === "refusedExpired") {
    body = <PostExpired oldConfirmation={view === "refusedExpired"} onStartNew={onStartNew} />
  } else if (view === "form" && correcting) {
    body = (
      <EmailCorrection waiting={waiting} cooldown={cooldown} onCooldown={setCooldown} onCorrected={corrected}
        correct={flow.correct} onSignIn={flow.signIn} words={flow.correction}
        onCancel={() => setCorrecting(false)}
        onUsedUp={() => setWaiting((w) => ({ ...w, resendsUsedUp: true }))}
        onRefusal={(err) => {
          setCorrecting(false)
          refuse(err)
        }} />
    )
  } else if (view === "form") {
    body = (
      <>
        {signedInEarly && <AuthAlert tone="quiet">{C.signInBeforeConfirm}</AuthAlert>}
        <AuthHeading title={flow.heading}>{C.sentTo(waiting.email)}</AuthHeading>
        {flow.lead && <p className={MUTED}>{flow.lead}</p>}
        {!waiting.sent && !notice && (
          <AuthAlert tone="bad">{undelivered ? E.deliveryFailed(waiting.email) : flow.deliveryFailed}</AuthAlert>
        )}
        {notice && <AuthAlert tone="good">{notice}</AuthAlert>}
        {alert && (
          <AuthAlert tone="bad">
            {alert}
            {support && (
              <a href={flow.supportHref} className="mt-1 block font-semibold underline underline-offset-2">{flow.supportLabel}</a>
            )}
          </AuthAlert>
        )}
        {locked && <AuthAlert tone="bad">{flow.codeLocked}</AuthAlert>}
        {waiting.resendsUsedUp && flow.usedUp && (
          <AuthAlert tone="bad">
            {flow.usedUp}
            {flow.supportHref && (
              <a href={flow.supportHref} className="mt-1 block font-semibold underline underline-offset-2">{flow.supportLabel}</a>
            )}
          </AuthAlert>
        )}

        <form onSubmit={submit} noValidate className="flex flex-col gap-[14px]">
          <div role="group" aria-labelledby="oh-code-label" aria-describedby="oh-code-help" className="flex flex-col gap-[7px]">
            <span id="oh-code-label" className="text-[13px] font-semibold text-foreground">{C.code.label}</span>
            <CodeInput key={codeKey} invalid={!!codeError} onChange={setCode} digitLabel={C.code.digitLabel} />
            <p id="oh-code-help" role={codeError ? "alert" : undefined}
              className={codeError ? "text-[12px] font-semibold text-primary" : "text-[12px] text-ink-3"}>
              {codeError ?? C.code.help(CODE_LENGTH)}
            </p>
          </div>
          <Button type="submit" disabled={submitting || resending || locked} className="gap-1.5">
            {submitting && <Loader2 className="size-4 animate-spin" />}
            {submitting ? C.submitting : flow.submit}
          </Button>
        </form>

        <p className={MUTED}>{C.linkHelp}</p>
        {!waiting.resendsUsedUp && (
          <div className="flex flex-col gap-2">
            <Button variant="outline" onClick={resend} disabled={resending || submitting || cooldown > 0} className="gap-1.5">
              {resending && <Loader2 className="size-4 animate-spin" />}
              {resending ? C.resending : undelivered ? E.resend : C.resend}
            </Button>
            {cooldown > 0 && <p className={MUTED}>{C.cooldown(cooldown)}</p>}
          </div>
        )}
        <Button variant="outline" disabled={resending || submitting} onClick={() => {
          setAlert(null)
          setNotice(null)
          setCorrecting(true)
        }}>
          {C.correctEmail}
        </Button>
        {waiting.deadlineShown && (
          <p className={MUTED}>
            {C.expiry(waiting.deadlineShown)} {C.expiryUnchanged}
          </p>
        )}
      </>
    )
  }

  return <WaitingColumn>{body}</WaitingColumn>
}
